import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MdMenu } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.text};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    padding: 1rem;
    gap: 0.5rem;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  
  a {
    color: inherit;
    text-decoration: none;
  }

  @media (max-width: 768px) {
    justify-self: center;
    text-align: center;
    font-size: 2rem;
    line-height: 1;
  }
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    justify-self: end;
    gap: 0.5rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  border: 1px solid rgba(252, 232, 232, 0.2);
  background-color: ${({ theme }) => theme.colors.light};
  color: ${({ theme }) => theme.colors.text};
  width: 40px;
  height: 40px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  padding: 0;

  svg {
    display: block;
  }

  @media (max-width: 768px) {
    display: inline-flex;
    justify-self: start;
  }
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.colors.light};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const UserButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 2rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 4px;
  padding: 0.5rem 0;
  min-width: 150px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 101;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  width: 100%;
  text-align: left;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const LanguageSelector = styled.select`
  background-color: ${({ theme }) => theme.colors.light};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    background-color: #000;
    color: #fff;
  }

  option {
    background-color: ${({ theme }) => theme.colors.dark};
    color: ${({ theme }) => theme.colors.text};
  }

  option:hover,
  option:focus,
  option:checked {
    background-color: #000;
    color: #fff;
  }
`;

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const language = e.target.value;
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  const getUserInitials = () => {
    if (!user) return '?';
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMobileMenuToggle = () => {
    window.dispatchEvent(new Event('sidebar:toggle-mobile'));
  };

  return (
    <HeaderContainer>
      <MobileMenuButton
        type="button"
        aria-label="Toggle navigation menu"
        onClick={handleMobileMenuToggle}
      >
        <MdMenu size={22} />
      </MobileMenuButton>
      <Logo>
        <Link to="/">Jhusef Lopez</Link>
      </Logo>
      
      <NavSection>
        <LanguageSelector onChange={handleLanguageChange} value={i18n.language}>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="ja">日本語</option>
          <option value="ar">العربية</option>
          <option value="de">Deutsch</option>
          <option value="hi">हिन्दी</option>
          <option value="ru">Русский</option>
          <option value="zh">中文</option>
        </LanguageSelector>
        
        {/*
          {isAuthenticated ? (
            <div ref={dropdownRef}>
              <UserButton onClick={() => setShowDropdown(!showDropdown)}>
                <UserAvatar>{getUserInitials()}</UserAvatar>
              </UserButton>
              
              <AnimatePresence>
                {showDropdown && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <DropdownItem onClick={() => {
                      setShowDropdown(false);
                    }}>
                      {t('common.profile')}
                    </DropdownItem>
                    <DropdownItem onClick={() => {
                      logout();
                      setShowDropdown(false);
                    }}>
                      {t('common.logout')}
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link to="/login">
              <Button>{t('common.login')}</Button>
            </Link>
          )}
        */}
      </NavSection>
    </HeaderContainer>
  );
};

export default Header; 